// src/pages/Procurement/POApprovalCentre.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import api from "@/lib/axios";
import { toast } from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";

import {
  Loader2,
  FileDown,
  CheckCircle2,
  XCircle,
  Activity,
  CalendarDays,
} from "lucide-react";

import POTimelineLog from "@/components/Procurement/POTimelineLog";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import {useAuth} from "@/context/AuthProvider";
import {useProcurementOperationalContext} from "@/hooks/useProcurementOperationalContext";
import {procurementContextKey} from "@/lib/procurementAssistantRequests";
import {verifyPoReviewSummary,poReviewFinancials,formatPoReviewDay} from "@/lib/poFinancialReview";
import POFinancialReview from "@/components/Procurement/POFinancialReview";

const NOTE_LIMIT = 1000;


function statusClasses(
  status = ""
) {
  const normalized =
    String(status || "")
      .toLowerCase();

  if (
    normalized.includes(
      "approved"
    )
  ) {
    return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  }

  if (
    normalized.includes(
      "pending finance"
    )
  ) {
    return "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30";
  }

  if (
    normalized.includes(
      "pending"
    )
  ) {
    return "bg-amber-500/15 text-amber-300 border-amber-500/30";
  }

  if (
    normalized.includes(
      "partially"
    )
  ) {
    return "bg-cyan-500/15 text-cyan-300 border-cyan-500/30";
  }

  return "bg-slate-500/15 text-slate-300 border-slate-500/30";
}

function safeText(
  value,
  fallback = "—"
) {
  if (
    value === null ||
    value === undefined
  ) {
    return fallback;
  }

  const text =
    String(value).trim();

  return text
    ? text
    : fallback;
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return safeText(value);
  }

  return date.toLocaleDateString();
}

function hasLinkedEvent(po) {
  return Boolean(
    po?.event_id ||
      po?.event_name
  );
}

/* ──────────────────────────────────────────────────────────────
 * PDF URL helper
 * ────────────────────────────────────────────────────────────── */

function buildApiUrl(pathname) {
  const baseURL =
    api?.defaults?.baseURL ||
    "/solo-mdsh-api";

  try {
    if (
      /^https?:\/\//i.test(
        baseURL
      )
    ) {
      const url =
        new URL(baseURL);

      const normalizedBasePath =
        url.pathname.replace(
          /\/+$/,
          ""
        );

      const normalizedPath =
        String(
          pathname || ""
        ).startsWith("/")
          ? pathname
          : `/${pathname || ""}`;

      url.pathname =
        `${normalizedBasePath}${normalizedPath}`;

      return url.toString();
    }

    const normalizedBase =
      String(baseURL).replace(
        /\/+$/,
        ""
      );

    const normalizedPath =
      String(
        pathname || ""
      ).startsWith("/")
        ? pathname
        : `/${pathname || ""}`;

    return `${normalizedBase}${normalizedPath}`;
  } catch {
    return pathname;
  }
}

/* ──────────────────────────────────────────────────────────────
 * PO card
 * ────────────────────────────────────────────────────────────── */

function GlowPOCard({
  po,
  onClick,
}) {
  return (
    <div
      onClick={onClick}
      className="group relative cursor-pointer"
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (
          event.key ===
            "Enter" ||
          event.key === " "
        ) {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <div className="absolute -inset-[2px] rounded-2xl bg-gradient-to-r from-cyan-500/40 via-fuchsia-500/40 to-emerald-500/40 blur-[2px] opacity-60 group-hover:opacity-100 transition" />

      <div className="relative rounded-2xl border border-white/10 bg-[#0b1120]/90 p-5 md:p-6 shadow-xl hover:shadow-2xl hover:bg-white/5 transition">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
          <div className="space-y-1">
            <div className="text-lg md:text-xl font-bold tracking-tight">
              {po.po_number ||
                `PO #${po.po_id}`}
            </div>

            <div className="text-slate-300 text-sm md:text-base">
              {po.vendor_name ||
                "—"}
            </div>

            {hasLinkedEvent(po) ? (
              <div className="flex flex-wrap gap-2 pt-1">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border border-emerald-500/30 text-emerald-300 bg-emerald-500/10">
                  <CalendarDays className="w-3.5 h-3.5" />

                  {po.event_name ||
                    `Event #${po.event_id}`}
                </span>

                {po.event_date ? (
                  <span className="px-2.5 py-1 rounded-full text-xs border border-emerald-500/20 text-emerald-200/90 bg-emerald-500/5">
                    {formatDate(
                      po.event_date
                    )}
                  </span>
                ) : null}
              </div>
            ) : null}

            {po.notes ? (
              <div className="text-xs md:text-sm text-slate-400 line-clamp-2">
                “{po.notes}”
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap md:justify-end gap-2">
            <span
              className={`px-2.5 py-1 rounded-full text-xs border ${statusClasses(
                po.status
              )}`}
            >
              {po.status}
            </span>

            {po.planner_name ? (
              <span className="px-2.5 py-1 rounded-full text-xs border border-white/10 text-slate-300 bg-white/5">
                Planner:{" "}
                {po.planner_name}
              </span>
            ) : null}

            <span className="px-2.5 py-1 rounded-full text-xs border border-white/10 text-slate-300 bg-white/5">
              Expected:{" "}
              {po.expected_delivery
                ? formatPoReviewDay(
                    po.expected_delivery
                  )
                : "—"}
            </span>

            <span className="px-2.5 py-1 rounded-full text-xs border border-white/10 text-slate-300 bg-white/5">
              Created:{" "}
              {po.created_at
                ? formatDate(
                    po.created_at
                  )
                : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Approval Centre
 * ────────────────────────────────────────────────────────────── */

export default function POApprovalCentre() {
  const {user}=useAuth();
  const {hasPermission}=useUserPermissions();
  const canApprove=hasPermission("procurement.po.approve");
  const operational=useProcurementOperationalContext(user,Boolean(user)&&canApprove);
  if(!canApprove)return null;
  if(operational.loading)return <p role="status">Loading selected company and unit…</p>;
  if(operational.error)return <p role="alert">{operational.error}</p>;
  const key=procurementContextKey(operational.expected);
  if(!key)return <p role="alert">Select a company and unit before reviewing POs.</p>;
  return <>
    {operational.refreshing&&<p role="status">Checking selected company and unit…</p>}
    <div hidden={operational.refreshing} inert={operational.refreshing?'':undefined}>
      <POApprovalSession key={key} expectedUser={operational.expected}/>
    </div>
  </>;
}

function POApprovalSession({expectedUser}) {
  const summaryRequest=useRef(null);
  useEffect(()=>()=>summaryRequest.current?.abort(),[]);

  /*
   * Server-resolved effective permission.
   *
   * No role assumptions are made here.
   */
  const {
    hasPermission,
  } =
    useUserPermissions();

  const canApprove =
    Boolean(
      hasPermission?.(
        "procurement.po.approve"
      )
    );

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    list,
    setList,
  ] =
    useState([]);

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    open,
    setOpen,
  ] =
    useState(false);

  const [
    activeId,
    setActiveId,
  ] =
    useState(null);

  const [
    summary,
    setSummary,
  ] =
    useState(null);

  const [
    sumLoading,
    setSumLoading,
  ] =
    useState(false);

  const [
    busy,
    setBusy,
  ] =
    useState(false);

  const [
    rejectReason,
    setRejectReason,
  ] =
    useState("");

  const [
    approvalNote,
    setApprovalNote,
  ] =
    useState("");

  /* ────────────────────────────────────────────────────────────
   * Pending PO loader
   *
   * IMPORTANT:
   *
   * Unauthorized users do not make the API request at all.
   *
   * Backend /purchase-orders/pending still remains authoritative
   * and independently returns 403 when permission is absent.
   * ──────────────────────────────────────────────────────────── */

  const loadPending =
    useCallback(
      async () => {
        if (!canApprove) {
          setLoading(false);
          setList([]);
          return;
        }

        setLoading(true);

        try {
          const {
            data,
          } =
            await api.get(
              "/purchase-orders/pending"
            );

          setList(
            Array.isArray(data)
              ? data
              : []
          );
        } catch (error) {
          console.error(
            "Failed to load pending POs:",
            error
          );

          setList([]);

          const status =
            error?.response?.status;

          if (
            status !== 401 &&
            status !== 403
          ) {
            toast.error(
              "Failed to load pending POs."
            );
          }
        } finally {
          setLoading(false);
        }
      },
      [
        canApprove,
      ]
    );

  useEffect(() => {
    if (!canApprove) {
      setLoading(false);
      setList([]);
      setOpen(false);
      setActiveId(null);
      setSummary(null);
      return;
    }

    loadPending();
  }, [
    canApprove,
    loadPending,
  ]);

  /* ────────────────────────────────────────────────────────────
   * Search/filter
   * ──────────────────────────────────────────────────────────── */

  const filtered =
    useMemo(() => {
      const query =
        String(search || "")
          .trim()
          .toLowerCase();

      if (!query) {
        return list;
      }

      return list.filter(
        (row) =>
          [
            row.po_id,
            row.po_number,
            row.vendor_name,
            row.status,
            row.planner_name,
            row.event_name,
            row.notes,
          ]
            .filter(
              (value) =>
                value !== null &&
                value !==
                  undefined
            )
            .some((value) =>
              String(value)
                .toLowerCase()
                .includes(query)
            )
      );
    }, [
      list,
      search,
    ]);

  /* ────────────────────────────────────────────────────────────
   * PO details
   * ──────────────────────────────────────────────────────────── */

  const openPO = async poId => {
    if (!canApprove || busy) return;
    summaryRequest.current?.abort();
    const controller = new AbortController();
    summaryRequest.current = controller;
    setOpen(true); setActiveId(poId); setSummary(null);
    setRejectReason(""); setApprovalNote(""); setSumLoading(true);
    try {
      const {data} = await api.get(`/purchase-orders/${poId}/summary`, {
        signal: controller.signal, params: {_ts: Date.now()},
      });
      const value = verifyPoReviewSummary(data, expectedUser, poId);
      if (!controller.signal.aborted) {
        setSummary(value); setApprovalNote(value.header.approval_note || "");
      }
    } catch (error) {
      if (!controller.signal.aborted) toast.error(error.message || "Failed to load PO details.");
    } finally { if (!controller.signal.aborted) setSumLoading(false); }
  };

  /* ────────────────────────────────────────────────────────────
   * Display totals
   *
   * Backend approval route independently calculates authoritative
   * PO gross value.
   * ──────────────────────────────────────────────────────────── */

  const financial = useMemo(() => poReviewFinancials(summary), [summary]);

  /* ────────────────────────────────────────────────────────────
   * Approve
   *
   * This deliberately does NOT call /po-dispatch/send.
   *
   * Final vendor dispatch belongs to the backend approval workflow.
   * Intermediate approval stages must never dispatch the PO.
   * ──────────────────────────────────────────────────────────── */

  const approve =
    async () => {
      if (
        !canApprove ||
        !activeId ||
        busy ||
        !financial.reviewable
      ) {
        return;
      }

      const note =
        String(
          approvalNote || ""
        ).trim();

      if (
        note.length >
        NOTE_LIMIT
      ) {
        toast.error(
          `Note too long (max ${NOTE_LIMIT} characters).`
        );

        return;
      }

      setBusy(true);

      try {
        const {
          data,
        } =
          await api.post(
            `/purchase-orders/${activeId}/approve`,
            {
              approval_note:
                note,
            }
          );

        /*
         * Backend message distinguishes:
         *
         * - intermediate approval / escalation
         * - final approval
         */
        toast.success(
          data?.message ||
            "PO approval processed."
        );

        setOpen(false);
        setActiveId(null);
        setSummary(null);
        setApprovalNote("");
        setRejectReason("");

        await loadPending();
      } catch (error) {
        console.error(
          "PO approval failed:",
          error
        );

        const data =
          error?.response?.data ||
          {};

        toast.error(
          data?.message ||
            data?.error ||
            "Approve failed."
        );
      } finally {
        setBusy(false);
      }
    };

  /* ────────────────────────────────────────────────────────────
   * Reject
   * ──────────────────────────────────────────────────────────── */

  const reject =
    async () => {
      if (
        !canApprove ||
        !activeId ||
        busy
      ) {
        return;
      }

      const reason =
        String(
          rejectReason || ""
        ).trim();

      if (!reason) {
        toast.error(
          "Please enter a rejection reason."
        );

        return;
      }

      setBusy(true);

      try {
        const response =
          await api.post(
            `/purchase-orders/${activeId}/reject`,
            {
              reason,
            }
          );

        if (
          !response ||
          (
            response.status !==
              200 &&
            response.status !==
              204 &&
            response?.data
              ?.ok !== true &&
            !response?.data
              ?.message
          )
        ) {
          throw new Error(
            response?.data
              ?.error ||
              "Reject failed."
          );
        }

        toast.success(
          response?.data
            ?.message ||
            "PO rejected."
        );

        setOpen(false);
        setActiveId(null);
        setSummary(null);
        setApprovalNote("");
        setRejectReason("");

        await loadPending();
      } catch (error) {
        console.error(
          "PO rejection failed:",
          error
        );

        toast.error(
          error?.response?.data
            ?.message ||
            error?.response?.data
              ?.error ||
            error?.message ||
            "Reject failed."
        );
      } finally {
        setBusy(false);
      }
    };

  /* ────────────────────────────────────────────────────────────
   * PDF
   * ──────────────────────────────────────────────────────────── */

  const viewPdf = () => {
    if (!activeId) {
      return;
    }

    try {
      const pdfUrl =
        buildApiUrl(
          `/purchase-orders/pdf/${activeId}`
        );

      const newWindow =
        window.open(
          pdfUrl,
          "_blank",
          "noopener,noreferrer"
        );

      if (!newWindow) {
        window.location.href =
          pdfUrl;
      }
    } catch (error) {
      console.error(
        "Failed to open PO PDF:",
        error
      );

      toast.error(
        "Failed to open PDF."
      );
    }
  };

  const handleSheetChange =
    (nextOpen) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        summaryRequest.current?.abort();
        setActiveId(null);
        setSummary(null);
        setSumLoading(false);
        setBusy(false);
        setRejectReason("");
        setApprovalNote("");
      }
    };

  const overLimit =
    approvalNote.length >
    NOTE_LIMIT;

  const rejectDisabled =
    busy ||
    !rejectReason.trim();

  /*
   * Normally this component can never render without permission
   * because App.jsx protects the route.
   *
   * This remains as defense-in-depth if the component is accidentally
   * mounted from another route in future.
   */
  if (!canApprove) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-black text-white p-8">
      {/* Header */}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            PO Approval Centre
          </h1>

          <p className="text-gray-400 mt-1">
            Review full details,
            view the PDF, then
            approve or reject.
          </p>
        </div>
      </div>

      {/* Search */}

      <div className="mb-5">
        <Input
          placeholder="Search by PO # / vendor / status…"
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          className="bg-slate-800 border-slate-700 text-white"
        />
      </div>

      {/* Pending POs */}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-300">
          <Loader2 className="animate-spin" />

          Loading pending POs…
        </div>
      ) : filtered.length ===
        0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-gray-300">
          No pending POs.
        </div>
      ) : (
        <div className="grid gap-5">
          {filtered.map(
            (po) => (
              <GlowPOCard
                key={po.po_id}
                po={po}
                onClick={() =>
                  openPO(
                    po.po_id
                  )
                }
              />
            )
          )}
        </div>
      )}

      {/* PO review sheet */}

      <Sheet
        open={open}
        onOpenChange={
          handleSheetChange
        }
      >
        <SheetContent className="w-full sm:max-w-3xl bg-slate-900 text-white border-slate-700 flex flex-col min-w-0">
          <SheetHeader>
            <SheetTitle>
              {summary?.header
                ?.po_number ||
                (
                  activeId
                    ? `PO #${activeId}`
                    : "Purchase Order"
                )}
            </SheetTitle>
          </SheetHeader>

          {sumLoading ? (
            <div className="mt-6 text-gray-300 flex items-center gap-2">
              <Loader2 className="animate-spin" />

              Loading details…
            </div>
          ) : !summary ? (
            <div className="mt-6 text-gray-400">
              No details.
            </div>
          ) : (
            <div className="mt-4 min-h-0 flex-1 space-y-6 overflow-auto pr-1">
              {/* Header details */}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-400">
                    Vendor
                  </div>

                  <div className="font-medium">
                    {summary.header
                      .vendor_name ||
                      "—"}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Status
                  </div>

                  <div className="font-medium">
                    {summary.header
                      .status ||
                      "—"}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Requested by
                  </div>

                  <div className="font-medium">
                    {summary.header
                      .created_by_name ||
                      "—"}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Expected delivery
                  </div>

                  <div className="font-medium">
                    {formatPoReviewDay(
                      summary.header
                        .expected_delivery
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Planner
                  </div>

                  <div className="font-medium">
                    {summary.header
                      .planner_name ||
                      "—"}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Cost centre
                  </div>

                  <div className="font-medium">
                    {summary.header
                      .cost_centre_name ||
                      summary.header
                        .cost_centre_id ||
                      "—"}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Linked event
                  </div>

                  <div className="font-medium">
                    {summary.header
                      .event_name ||
                      (
                        summary.header
                          .event_id
                          ? `Event #${summary.header.event_id}`
                          : "—"
                      )}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400">
                    Event date
                  </div>

                  <div className="font-medium">
                    {formatDate(
                      summary.header
                        .event_date
                    )}
                  </div>
                </div>
              </div>

              {/* PO notes */}

              {summary.header
                .notes ? (
                <div className="text-sm text-gray-300">
                  “
                  {
                    summary
                      .header
                      .notes
                  }
                  ”
                </div>
              ) : null}

              <POFinancialReview summary={summary} />

              {/* Activity */}

              <div className="mt-4">
                <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                  <Activity className="w-4 h-4" />

                  Activity
                </div>

                <POTimelineLog
                  poId={activeId}
                />
              </div>
            </div>
          )}

          {/* Approval controls */}

          <SheetFooter className="mt-auto shrink-0 flex-col gap-3 sm:flex-col">
            <div className="flex flex-wrap items-center justify-between w-full gap-3">
              <Button
                variant="secondary"
                onClick={
                  viewPdf
                }
                disabled={
                  !activeId ||
                  sumLoading ||
                  !summary
                }
              >
                <FileDown className="w-4 h-4 mr-2" />

                View PDF
              </Button>

              <div className="flex items-center gap-2">
                <Button
                  onClick={
                    approve
                  }
                  disabled={
                    busy ||
                    overLimit ||
                    !activeId ||
                    sumLoading ||
                    !summary ||
                    !financial.reviewable
                  }
                  className={`text-black ${
                    busy ||
                    overLimit ||
                    !financial.reviewable
                      ? "bg-green-800"
                      : "bg-green-500 hover:bg-green-400"
                  }`}
                  title={
                    overLimit
                      ? `Note too long (max ${NOTE_LIMIT})`
                      : ""
                  }
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />

                  {busy
                    ? "Working…"
                    : "Approve"}
                </Button>

                <Button
                  variant="destructive"
                  onClick={
                    reject
                  }
                  disabled={
                    rejectDisabled ||
                    !activeId ||
                    sumLoading ||
                    !summary
                  }
                  title={
                    !rejectReason.trim()
                      ? "Rejection reason is required"
                      : ""
                  }
                >
                  <XCircle className="w-4 h-4 mr-1" />

                  Reject
                </Button>
              </div>
            </div>

            {/* Approval/rejection text */}

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-gray-300 mb-1">
                  Note to vendor
                  (optional) —
                  appears on PDF &
                  email
                </div>

                <Textarea
                  value={
                    approvalNote
                  }
                  onChange={(
                    event
                  ) =>
                    setApprovalNote(
                      event.target
                        .value
                    )
                  }
                  placeholder="e.g., Please deliver before 9am and include chilled packs."
                  className={`bg-slate-800 border ${
                    overLimit
                      ? "border-red-500"
                      : "border-slate-700"
                  } text-white`}
                  maxLength={
                    NOTE_LIMIT
                  }
                  disabled={
                    busy ||
                    !summary
                  }
                />

                <div
                  className={`text-right text-[11px] mt-1 ${
                    overLimit
                      ? "text-red-400"
                      : "text-gray-400"
                  }`}
                >
                  {
                    approvalNote.length
                  }{" "}
                  /{" "}
                  {
                    NOTE_LIMIT
                  }
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-300 mb-1">
                  Rejection reason
                  (required only if
                  rejecting)
                </div>

                <Textarea
                  value={
                    rejectReason
                  }
                  onChange={(
                    event
                  ) =>
                    setRejectReason(
                      event.target
                        .value
                    )
                  }
                  placeholder="If rejecting, tell the requester why (required)"
                  className={`bg-slate-800 text-white border ${
                    !rejectReason.trim()
                      ? "border-red-500/60"
                      : "border-slate-700"
                  }`}
                  disabled={
                    busy ||
                    !summary
                  }
                />

                {!rejectReason.trim() &&
                summary ? (
                  <div className="text-[11px] mt-1 text-red-400">
                    Rejection
                    reason is
                    required to
                    reject.
                  </div>
                ) : null}
              </div>
            </div>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
